/**
 * Picking Verification Hooks
 *
 * React Query hooks for picking verification functionality.
 */
import { trpc } from '@/lib/trpc';
import { useLocation } from '@/contexts/LocationContext';
import type { PickingVerificationMode } from '../types';

/**
 * Hook to fetch batch pick progress with live polling.
 * Polls every 5 seconds for real-time updates.
 */
export function usePickProgress(batchId: string | undefined) {
  return trpc.picking.getBatchPickProgress.useQuery(
    { batchId: batchId! },
    {
      enabled: !!batchId,
      refetchInterval: 5000, // Poll every 5s for live updates
      refetchIntervalInBackground: false, // Battery optimization: pause when tab hidden
    }
  );
}

/**
 * Hook to verify a pick by QR code scan.
 * Invalidates progress query on success.
 */
export function useVerifyPick(batchId: string) {
  const utils = trpc.useUtils();
  const { selectedLocation } = useLocation();

  return trpc.picking.verifyPickByQR.useMutation({
    onSuccess: () => {
      // Invalidate pick progress to refresh counts
      utils.picking.getBatchPickProgress.invalidate({ batchId });
    },
    // Build input with closure over location
    mutationKey: ['picking', 'verifyPick', batchId, selectedLocation?.id],
  });
}

/**
 * Build verify pick input for the mutation
 */
export function buildVerifyPickInput(qrCode: string, batchId: string, locationId: string) {
  return { qrCode, batchId, locationId };
}

/**
 * Hook to get the picking verification mode for current location.
 * Caches for 1 minute since this changes infrequently.
 */
export function useLocationPickingMode() {
  const { selectedLocation } = useLocation();

  return trpc.picking.getLocationPickingMode.useQuery(
    { locationId: selectedLocation?.id ?? '' },
    {
      enabled: !!selectedLocation?.id,
      staleTime: 60000, // Cache for 1 minute
    }
  );
}

/**
 * Hook to update the picking verification mode for a location.
 */
export function useUpdateLocationPickingMode() {
  const utils = trpc.useUtils();

  return trpc.picking.updateLocationPickingMode.useMutation({
    onSuccess: () => {
      utils.picking.getLocationPickingMode.invalidate();
    },
  });
}

/**
 * Build update picking mode input for the mutation
 */
export function buildUpdateModeInput(locationId: string, mode: PickingVerificationMode) {
  return { locationId, mode };
}

/**
 * Hook to bulk-verify a group of picks by garment IDs.
 * Used by 'none' verification mode when a picklist checkbox is ticked.
 */
export function useVerifyPickGroup(batchId: string) {
  const utils = trpc.useUtils();

  return trpc.picking.verifyPickGroup.useMutation({
    onSuccess: () => {
      utils.picking.getBatchPickProgress.invalidate({ batchId });
    },
  });
}

/**
 * Hook to undo a pick verification.
 */
export function useUndoPick(batchId: string) {
  const utils = trpc.useUtils();

  return trpc.picking.undoPick.useMutation({
    onSuccess: () => {
      utils.picking.getBatchPickProgress.invalidate({ batchId });
    },
  });
}

/**
 * Hook to confirm PAP item placement (bin/shelf QR scanned).
 * Called after PlacementOverlay is shown for PAP items.
 * Invalidates pick progress on success since status advances.
 */
export function useConfirmPapPlacement(batchId: string) {
  const utils = trpc.useUtils();

  return trpc.picking.confirmPapPlacement.useMutation({
    onSuccess: () => {
      utils.picking.getBatchPickProgress.invalidate({ batchId });
    },
  });
}
